import { useAuth, useClerk } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { isClerkEnabled } from '@/components/auth-gate';
import { type LoginPromptContext } from '@/api/loginRequired';
import {
  trackLoginGateShown,
  trackLoginGateUnavailable,
  type LoginGateUnavailableReason,
} from '@/lib/posthog';

/**
 * A.6.6c — sign in before paid cloud work, not after a failed request.
 *
 * `guard(action)` runs `action` right away for a signed-in visitor. For a
 * signed-out visitor it records `login_gate_shown`, opens Clerk's sign-up
 * modal, and parks `action`; once sign-in completes on the same page the
 * parked action runs, so the staged file carries through. Nothing reaches the
 * server before that. `locked` drives the signed-out button copy
 * ("Sign in to convert").
 *
 * With Clerk disabled (no-key dev boot, self-host) there is no account to ask
 * for, so the gate is open.
 *
 * W.4.116 (#2689): when the Clerk script never loads (a content blocker, a
 * filtered network), `isLoaded` stays false and a parked action used to wait
 * forever: the button did nothing. A click now waits at most
 * `LOGIN_GATE_WAIT_MS` (no wait when Clerk already reports `status: 'error'`),
 * then records `login_gate_unavailable` and shows `LoginGateUnavailableNotice`.
 */
export type LoginGate = {
  /** Signed out: the primary action opens sign-up instead of running. */
  locked: boolean;
  /** Run `action` now if signed in; otherwise prompt sign-up and resume after. */
  guard: (action: () => void) => void;
};

const OPEN_GATE: LoginGate = {
  locked: false,
  guard: (action) => action(),
};

/** How long a click waits for the account script before the visitor is told. */
export const LOGIN_GATE_WAIT_MS = 8_000;

// One notice for the page, whichever gated control asked. `main.tsx` mounts
// the notice once inside ClerkProvider.
let unavailableShown = false;
const unavailableListeners = new Set<() => void>();

function setLoginGateUnavailable(shown: boolean): void {
  if (unavailableShown === shown) return;
  unavailableShown = shown;
  for (const listener of unavailableListeners) listener();
}

function subscribeLoginGateUnavailable(listener: () => void): () => void {
  unavailableListeners.add(listener);
  return () => unavailableListeners.delete(listener);
}

/** True while the page should say that sign-in could not load. */
export function useLoginGateUnavailable(): boolean {
  return useSyncExternalStore(
    subscribeLoginGateUnavailable,
    () => unavailableShown,
    () => false,
  );
}

export function dismissLoginGateUnavailable(): void {
  setLoginGateUnavailable(false);
}

function currentReturnUrl(): string {
  return typeof window !== 'undefined' ? window.location.href : '/';
}

function useClerkLoginGate(context: LoginPromptContext): LoginGate {
  const { isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();
  const { openSignUp } = clerk;
  const pendingRef = useRef<(() => void) | null>(null);
  const waitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reportedRef = useRef(false);
  // Bumped when an action is parked so the resume effect re-checks auth even
  // if Clerk had already settled.
  const [pendingTick, setPendingTick] = useState(0);
  const { surface, tool } = context;

  const prompt = useCallback(() => {
    trackLoginGateShown({ surface, tool, trigger: 'preflight' });
    // Both directions return here: the modal lets the visitor change to
    // sign-in, and a sign-up with an existing Google account becomes a sign-in
    // on the way. Without the second URL that visitor lands on the app home.
    const returnUrl = currentReturnUrl();
    void openSignUp({ forceRedirectUrl: returnUrl, signInForceRedirectUrl: returnUrl });
  }, [openSignUp, surface, tool]);

  const reportUnavailable = useCallback(
    (reason: LoginGateUnavailableReason) => {
      if (!reportedRef.current) {
        reportedRef.current = true;
        trackLoginGateUnavailable({ surface, tool, reason });
      }
      setLoginGateUnavailable(true);
    },
    [surface, tool],
  );

  const guard = useCallback(
    (action: () => void) => {
      if (isLoaded && isSignedIn) {
        action();
        return;
      }
      pendingRef.current = action;
      if (!isLoaded) {
        // Auth still resolving: decide once Clerk loads (effect below).
        setPendingTick((tick) => tick + 1);
        if (clerk.status === 'error') {
          reportUnavailable('clerk_load_error');
        } else if (waitRef.current === null) {
          waitRef.current = setTimeout(() => {
            waitRef.current = null;
            reportUnavailable('clerk_load_timeout');
          }, LOGIN_GATE_WAIT_MS);
        }
        return;
      }
      prompt();
    },
    [clerk, isLoaded, isSignedIn, prompt, reportUnavailable],
  );

  // Clerk loaded after all: stop the wait and take the notice down.
  useEffect(() => {
    if (!isLoaded) return;
    if (waitRef.current !== null) {
      clearTimeout(waitRef.current);
      waitRef.current = null;
    }
    setLoginGateUnavailable(false);
  }, [isLoaded]);

  useEffect(
    () => () => {
      if (waitRef.current !== null) clearTimeout(waitRef.current);
    },
    [],
  );

  // Sync with Clerk's auth state: resume the parked action after sign-in, or
  // open sign-up if the visitor turned out to be signed out once Clerk loaded.
  useEffect(() => {
    if (!isLoaded || !pendingRef.current) return;
    if (isSignedIn) {
      const action = pendingRef.current;
      pendingRef.current = null;
      action();
      return;
    }
    if (pendingTick > 0) {
      setPendingTick(0);
      prompt();
    }
  }, [isLoaded, isSignedIn, pendingTick, prompt]);

  return { locked: isLoaded && !isSignedIn, guard };
}

function useOpenLoginGate(_context: LoginPromptContext): LoginGate {
  return OPEN_GATE;
}

export function useLoginGate(context: LoginPromptContext): LoginGate {
  // isClerkEnabled() reads a build-time env constant, so the same hook runs on
  // every render; the branch only keeps Clerk hooks out of no-Clerk boots,
  // where ClerkProvider is absent and useAuth() would throw.
  const useGate = isClerkEnabled() ? useClerkLoginGate : useOpenLoginGate;
  return useGate(context);
}
